import { QRCodeCanvas, QRCodeSVG } from 'qrcode.react';

/**
 * The only file that imports qrcode.react. Every QR code in the app goes
 * through this component, so swapping or upgrading the library is a
 * one-file change.
 */

// The flat DMD "D" mark (assets/dmd/dmd-d-flat.svg)
const DMD_D_VIEWBOX = '-40 -105 654 654';
const DMD_D_PATH =
  'M311.361 10.1123H10V81.1343H96.96V81.0383H311.903C405.675 81.0383 477.672 142.078 477.672 221.982C477.672 301.886 404.603 362.938 310.831 362.938H96.96V143.441H10V434.128H311.361C458.104 434.128 563.505 340.81 563.505 217.746C563.505 94.6823 458.104 10.1123 311.361 10.1123Z';

/**
 * Build a data URL for the DMD "D" in the given color, for the center of a
 * QR code
 */
const dmdMarkDataUrl = (params: { color: string }) => {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${DMD_D_VIEWBOX}"><path d="${DMD_D_PATH}" fill="${params.color}" stroke="${params.color}" stroke-width="20"/></svg>`;
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
};

/**
 * Render a QR code.
 *
 * @param value - The text or URL to encode
 * @param size - Width and height in px
 * @param logoColor - When set, the DMD "D" is drawn in the center in this
 *   color (navy #052a63 in the design), and error correction is raised to H
 *   so the code still scans
 * @param renderAs - 'svg' (default) or 'canvas'
 * @param title - Accessible name for the code
 */
export function QrCode(props: {
  value: string;
  size: number;
  logoColor?: string;
  renderAs?: 'svg' | 'canvas';
  title?: string;
}) {
  // Props
  const { value, size, logoColor } = props;

  // The logo covers about a fifth of the code; level H tolerates ~30% loss
  const logoSize = Math.round(size * 0.22);
  const imageSettings = logoColor
    ? {
        src: dmdMarkDataUrl({ color: logoColor }),
        height: logoSize,
        width: logoSize,
        excavate: true,
      }
    : undefined;

  const shared = {
    value,
    size,
    // L matches qrcode.react's default, so existing codes render unchanged
    level: logoColor ? ('H' as const) : ('L' as const),
    marginSize: 0,
    title: props.title,
    imageSettings,
  };

  if (props.renderAs === 'canvas') {
    return <QRCodeCanvas {...shared} />;
  }
  return <QRCodeSVG {...shared} />;
}
